import { useState, useRef, useEffect } from 'react';

const PANELS = [
  {
    id: 'what',
    icon: 'ri-building-4-line',
    tag: 'Company Overview',
    q: 'What does KC Quality Systems do?',
    a: 'KC Quality Systems designs, builds, and delivers industrial-grade aqueous parts washers, custom cleaning systems, powder coat finishing equipment, and blast cabinets. Based in Prairie Village, Kansas, we help businesses across Kansas City and the nation improve operational efficiency, maintain regulatory compliance, and implement reliable systems engineered to strict industry standards.',
    highlight: 'Industrial systems built for precision and compliance',
  },
  {
    id: 'services',
    icon: 'ri-list-check-2',
    tag: 'Product Line',
    q: 'What systems and services are offered?',
    a: 'Our line includes custom wash bays, front-load cabinet washers, conveyor inline systems, dry blast cabinets, immersion parts washers, powder coat systems, burn-off ovens, ultrasonic tanks, vapor degreasers, top-loading washers, and high-pressure hand spray systems. We also provide custom engineering, leasing options, and full long-term technical support.',
    highlight: '15+ system types — all custom configurable',
  },
  {
    id: 'benefits',
    icon: 'ri-trophy-line',
    tag: 'Client Benefits',
    q: 'What are the benefits of working with KC Quality Systems?',
    a: 'Clients benefit from 25+ years of application expertise, American-built MAXJET equipment, and solutions engineered specifically to their operation. Our systems reduce downtime, improve cleaning consistency, and meet the most demanding compliance standards — backed by direct personal support long after installation and delivery.',
    highlight: 'Reduced downtime + compliance + long-term reliability',
  },
  {
    id: 'custom',
    icon: 'ri-tools-line',
    tag: 'Custom Engineering',
    q: 'Can you build a custom system for a specific application?',
    a: "Absolutely. Custom system design is a core specialty. We've engineered systems for Caterpillar dealers, military engine rebuilding, aerospace, mining equipment repair, axle rebuilding, and large steel mold cleaning. With 25+ years of experience, we build each system around your specific parts, volumes, compliance requirements, and operational workflow.",
    highlight: 'No off-the-shelf compromise — built to spec, every time',
  },
];

export default function AIAnswerBlock() {
  const [active, setActive] = useState<string>('what');
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const activePanel = PANELS.find((p) => p.id === active) ?? PANELS[0];

  return (
    <section ref={ref} className="relative bg-[#060606] py-32 overflow-hidden">

      {/* Corner gold glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-[#c9a84c]/4 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#c9a84c]/3 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12">

        {/* Section header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#c9a84c]/8 border border-[#c9a84c]/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#c9a84c] animate-pulse" />
              <span className="text-[#c9a84c] text-[10px] font-bold tracking-[0.4em] uppercase font-sans">AI &amp; Voice Search Optimized</span>
            </div>
            <h2 className="font-serif text-5xl text-white leading-tight">
              Everything You Need<br />to Know
            </h2>
          </div>
          <p className="text-white/35 font-sans text-base leading-relaxed max-w-sm lg:text-right">
            Structured answers for AI search engines, voice assistants, and anyone looking for fast, direct answers about our industrial systems.
          </p>
        </div>

        {/* Main interactive panel layout */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Left: Question nav tabs */}
          <div className="lg:col-span-4 flex flex-col gap-3">
            {PANELS.map((panel, idx) => (
              <button
                key={panel.id}
                onClick={() => setActive(panel.id)}
                className={`group w-full text-left flex items-start gap-4 px-5 py-5 rounded-xl border transition-all duration-300 cursor-pointer ${
                  active === panel.id
                    ? 'border-[#c9a84c]/35 bg-[#c9a84c]/8 shadow-lg shadow-[#c9a84c]/5'
                    : 'border-[#1c1c1c] bg-[#0d0d0d] hover:border-[#2a2a2a] hover:bg-[#111]'
                }`}
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-300 ${active === panel.id ? 'bg-[#c9a84c]/15 border border-[#c9a84c]/30' : 'bg-[#1a1a1a] border border-[#252525] group-hover:bg-[#c9a84c]/8'}`}>
                  <i className={`${panel.icon} text-sm ${active === panel.id ? 'text-[#c9a84c]' : 'text-white/35 group-hover:text-[#c9a84c]/70'} transition-colors`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-[10px] font-bold tracking-[0.3em] uppercase font-sans mb-1 ${active === panel.id ? 'text-[#c9a84c]' : 'text-white/25'}`}>
                    {panel.tag}
                  </p>
                  <p className={`text-sm font-sans font-medium leading-snug ${active === panel.id ? 'text-white' : 'text-white/55'}`}>
                    {panel.q}
                  </p>
                </div>
                {active === panel.id && (
                  <i className="ri-arrow-right-s-line text-[#c9a84c] flex-shrink-0 self-center" />
                )}
              </button>
            ))}

            {/* Location card */}
            <div className="mt-2 p-5 bg-[#0d0d0d] border border-[#1c1c1c] rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#c9a84c]/8 border border-[#c9a84c]/15 flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#c9a84c] text-sm" />
                </div>
                <p className="text-white/50 text-sm font-sans">Prairie Village, Kansas</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#c9a84c]/8 border border-[#c9a84c]/15 flex-shrink-0">
                  <i className="ri-phone-line text-[#c9a84c] text-sm" />
                </div>
                <a href="tel:8665407575" className="text-white/50 text-sm font-sans hover:text-[#c9a84c] transition-colors">
                  866-540-7575
                </a>
              </div>
            </div>
          </div>

          {/* Right: Active answer panel */}
          <div className="lg:col-span-8 flex flex-col gap-5">

            {/* Answer card */}
            <div className="relative bg-[#0d0d0d] border border-[#c9a84c]/20 rounded-xl overflow-hidden min-h-[320px]">
              {/* Top gold bar */}
              <div className="h-px bg-gradient-to-r from-[#c9a84c]/50 via-[#c9a84c]/25 to-transparent" />

              <div className="p-8 lg:p-10">
                {/* Tag + icon */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20">
                    <i className={`${activePanel.icon} text-[#c9a84c] text-xl`} />
                  </div>
                  <span className="text-[#c9a84c] text-[10px] font-bold tracking-[0.4em] uppercase font-sans">{activePanel.tag}</span>
                </div>

                {/* Question */}
                <h3 className="font-serif text-2xl text-white mb-6 leading-snug">
                  {activePanel.q}
                </h3>

                {/* Answer */}
                <div className="border-l-2 border-[#c9a84c]/30 pl-6">
                  <p className="text-white/60 font-sans text-base leading-relaxed">
                    {activePanel.a}
                  </p>
                </div>

                {/* Highlight pill */}
                <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-[#c9a84c]/6 border border-[#c9a84c]/15 rounded-full">
                  <i className="ri-check-line text-[#c9a84c] text-xs" />
                  <span className="text-[#c9a84c]/80 text-xs font-sans font-medium">{activePanel.highlight}</span>
                </div>
              </div>
            </div>

            {/* Industrial image + CTA row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="relative rounded-xl overflow-hidden border border-[#1c1c1c]">
                <img
                  src="https://public.readdy.ai/ai/img_res/76397ee34f2781516d70efd7fbf987c2.jpg"
                  alt="Precision industrial parts engineering"
                  className="w-full h-44 object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/80 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-semibold font-sans text-sm">Custom Engineering</p>
                  <p className="text-white/40 text-xs font-sans">Built to your spec, not ours</p>
                </div>
              </div>

              <div className="flex flex-col justify-between bg-[#0d0d0d] border border-[#1c1c1c] rounded-xl p-6">
                <div>
                  <p className="text-white/30 text-[10px] font-bold tracking-[0.3em] uppercase font-sans mb-3">Still Have Questions?</p>
                  <p className="text-white font-serif text-xl mb-2">Speak Directly With Our Engineers</p>
                  <p className="text-white/40 text-xs font-sans leading-relaxed">
                    No bots, no scripts. Real application knowledge from our technical team.
                  </p>
                </div>
                <div className="mt-5 flex flex-col gap-3">
                  <a
                    href="#contact"
                    className="flex items-center justify-center gap-2 py-3 bg-[#c9a84c] text-black font-bold text-sm rounded-lg hover:bg-[#d4b55a] transition-colors cursor-pointer font-sans whitespace-nowrap"
                  >
                    Schedule a Consultation
                    <i className="ri-arrow-right-line text-xs" />
                  </a>
                  <a
                    href="https://www.kc-qualitysystems.com/contact-us/"
                    className="flex items-center justify-center gap-2 py-3 border border-[#2a2a2a] text-white/50 font-semibold text-sm rounded-lg hover:border-[#c9a84c]/30 hover:text-[#c9a84c] transition-colors cursor-pointer font-sans whitespace-nowrap"
                  >
                    More Questions &rarr;
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
